import React, { useEffect, useRef } from "react";

import { useCreativeChat } from "../../../services/useCreativeChat";

/**
 * The creative session's chat assistant, in v2.
 *
 * WAVE 4. **v2 had no chat panel at all** — the wizard shipped without the one
 * surface that lets an operator ask for a change in their own words. This is
 * it, on `services/useCreativeChat`, shared with v1's panel.
 *
 * THE DEFECT THAT SHAPED THIS COMPONENT: v1 pushed a failed send into the
 * transcript as `role: 'assistant'` with "Sorry, something went wrong." — the
 * assistant's bubble, the assistant's voice, indistinguishable from a real
 * answer. So this renders three kinds of row, and a failure can never wear the
 * assistant's clothes: `user`, `assistant`, and `failure`, which is a role the
 * backend never returns.
 */
export default function ChatPanel({ sessionId, currentStep, stepLabel, getStepContent }) {
  const C = useCreativeChat({ sessionId, currentStep, getStepContent });
  const endRef = useRef(null);

  // `?.` guards a null ref, not a missing METHOD. `scrollIntoView` does not
  // exist in every environment, and a chat panel that throws while trying to
  // scroll is worse than one that does not scroll — the whole panel unmounts
  // and the conversation goes with it.
  useEffect(() => {
    const el = endRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, [C.messages]);

  if (!sessionId) return null;

  return (
    <aside className="ws-chat" data-testid="ws-chat">
      <header className="ws-chat-head">
        <span className="ws-chat-title">Creative Agent</span>
        <span className="dim">{C.messages.length} msg{C.messages.length === 1 ? "" : "s"}</span>
      </header>

      <div className="ws-chat-body">
        {C.messages.length === 0 ? (
          <div className="ws-chat-empty">
            <p className="dim">
              Ask for a change, give direction, or talk through the step you are
              on{stepLabel ? ` — ${stepLabel}` : ""}.
            </p>
            <button type="button" className="btn-ghost btn-sm"
                    onClick={C.loadHistory}
                    disabled={C.historyState === "loading"}
                    data-testid="ws-chat-load-history">
              {C.historyState === "loading" ? "Loading…" : "Load previous messages"}
            </button>
            {/* A failed history read is NOT an empty history. */}
            {C.historyState === "failed" ? (
              <p className="state-error" role="alert" data-testid="ws-chat-history-failed">
                Previous messages could not be read: {C.historyError}. This
                session may still have them.
              </p>
            ) : null}
            {C.historyState === "ok" ? (
              <p className="dim" data-testid="ws-chat-history-empty">
                No previous messages in this session.
              </p>
            ) : null}
          </div>
        ) : (
          <ul className="ws-chat-list">
            {C.messages.map((m) => (
              <li key={m.message_id}
                  className={`ws-chat-msg is-${m.role}`}
                  data-testid={`ws-chat-msg-${m.role}-${m.message_id}`}>
                {m.role === "failure" ? (
                  <p className="state-error" role="alert">
                    That message did not reach the assistant: {m.content}. Your
                    text is back in the box — nothing was sent.
                  </p>
                ) : (
                  <p>{m.content}</p>
                )}
              </li>
            ))}
            <li ref={endRef} />
          </ul>
        )}
      </div>

      {C.transcriptNote ? (
        <p className="dim ws-chat-note" data-testid="ws-chat-transcript-note">
          {C.transcriptNote}
        </p>
      ) : null}

      <div className="ws-chat-compose">
        <textarea
          rows={2}
          value={C.input}
          onChange={(e) => C.setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); C.send(); }
          }}
          placeholder={`Message about ${stepLabel || "this step"}…`}
          data-testid="ws-chat-input"
        />
        <button type="button" className="btn-primary btn-sm"
                onClick={C.send}
                disabled={C.sending || !C.input.trim()}
                data-testid="ws-chat-send">
          {C.sending ? "Sending…" : "Send"}
        </button>
      </div>
      <p className="dim ws-chat-hint">Enter to send, Shift+Enter for a new line.</p>
    </aside>
  );
}
